'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { SectionHeader } from '@/features/admin/presentation/components/SectionHeader';
import { PlanAdminCard } from '@/features/plans/presentation/admin/components/PlanAdminCard';
import { PlanFormModal } from '@/features/plans/presentation/admin/components/PlanFormModal';
import { Button } from '@/core/presentation/components/ui/Button';
import { useToast } from '@/core/presentation/components/Toast';
import { usePlans } from '@/features/plans/presentation/context/PlansContext';
import type { PlanKind } from '@/core/domain';
import {
  PLAN_ADMIN_EMPTY_LIST,
  PLAN_ADMIN_PAGE_SUBTITLE,
  PLAN_ADMIN_TOAST_ACTIVATED,
  PLAN_ADMIN_TOAST_DEACTIVATED,
  PLAN_ADMIN_TOAST_DELETED,
  PLAN_ADMIN_TOAST_LOAD_FAILED,
} from '@/features/plans/presentation/composables/constants/plan-admin.constants';
import { Plus } from 'lucide-react';

function sortPlans(a: { sort_order: number; title: string }, b: { sort_order: number; title: string }) {
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return a.title.localeCompare(b.title, 'pt-BR');
}

export default function PlansAdminPage() {
  const toast = useToast();
  const {
    state: { plans, loading, error },
    events,
  } = usePlans();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [createKind, setCreateKind] = useState<PlanKind>('paid');

  useEffect(() => {
    void events.refresh();
  }, [events.refresh]);

  useEffect(() => {
    if (!error) return;
    toast.error(PLAN_ADMIN_TOAST_LOAD_FAILED);
    events.clearError();
  }, [error, events, toast]);

  const activePlans = useMemo(() => plans.filter((p) => p.is_active).sort(sortPlans), [plans]);
  const inactivePlans = useMemo(() => plans.filter((p) => !p.is_active).sort(sortPlans), [plans]);

  const openCreate = useCallback((kind: PlanKind) => {
    setCreateKind(kind);
    setEditingPlanId(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((id: string) => {
    setEditingPlanId(id);
    setModalOpen(true);
  }, []);

  const handleToggleActive = useCallback(
    async (id: string, isActive: boolean) => {
      const result = await events.update(id, {
        is_active: isActive,
        updated_at: new Date().toISOString(),
      });
      if (result.success) {
        toast.success(isActive ? PLAN_ADMIN_TOAST_ACTIVATED : PLAN_ADMIN_TOAST_DEACTIVATED);
      } else if (result.error) {
        toast.error(result.error);
      }
    },
    [events, toast]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const result = await events.delete(id);
      if (result.success) {
        toast.success(PLAN_ADMIN_TOAST_DELETED);
      } else if (result.error) {
        toast.error(result.error);
      }
    },
    [events, toast]
  );

  return (
    <div>
      <SectionHeader
        title="Planos"
        subtitle={PLAN_ADMIN_PAGE_SUBTITLE}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="border-admin-border text-admin-text"
              onClick={() => openCreate('free')}
              disabled={loading}
            >
              <Plus className="h-4 w-4 mr-1" />
              Plano gratuito
            </Button>
            <Button
              className="bg-admin-navy hover:bg-admin-navy-mid text-white"
              onClick={() => openCreate('paid')}
              disabled={loading}
            >
              <Plus className="h-4 w-4 mr-1" />
              Plano pago
            </Button>
          </div>
        }
      />

      {error ?
        <p className="text-sm text-destructive mb-4" role="alert">
          {error}
        </p>
      : null}

      {loading && plans.length === 0 ?
        <p className="text-sm text-admin-text-secondary py-8">Carregando planos…</p>
      : null}

      {!loading && plans.length === 0 ?
        <p className="text-sm text-admin-text-secondary py-8">{PLAN_ADMIN_EMPTY_LIST}</p>
      : null}

      {activePlans.length > 0 ?
        <section className="mb-10">
          <h2 className="text-sm font-semibold text-admin-text mb-3">
            Ativos{' '}
            <span className="font-normal text-admin-text-secondary">({activePlans.length})</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
            {activePlans.map((plan) => (
              <PlanAdminCard
                key={plan.id}
                plan={plan}
                busy={loading}
                onEdit={openEdit}
                onToggleActive={handleToggleActive}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </section>
      : null}

      {inactivePlans.length > 0 ?
        <section>
          <h2 className="text-sm font-semibold text-admin-text mb-3">
            Inativos{' '}
            <span className="font-normal text-admin-text-secondary">({inactivePlans.length})</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
            {inactivePlans.map((plan) => (
              <PlanAdminCard
                key={plan.id}
                plan={plan}
                busy={loading}
                onEdit={openEdit}
                onToggleActive={handleToggleActive}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </section>
      : null}

      <PlanFormModal
        open={modalOpen}
        onOpenChange={(open) => {
          setModalOpen(open);
          if (!open) setEditingPlanId(null);
        }}
        planId={editingPlanId}
        initialKind={createKind}
      />
    </div>
  );
}
